// ---------------------------------------------------------------------------
// The Yaghi logo.
//
// It loads the artwork from /images/yaghi-logo.png. If that file is not there,
// it quietly falls back to a typographic wordmark in the brand colours, so no
// screen ever shows a broken-image icon.
//
// TO ADD THE REAL LOGO: save the artwork as
//     frontend/public/images/yaghi-logo.png
// No code change is needed - it appears everywhere as soon as the file exists.
//
// PROPORTIONS ARE NEVER TOUCHED. Only a height is ever set; the width is left
// to follow the artwork's own aspect ratio, and `object-contain` guarantees
// the image is fitted rather than cropped or stretched. The one thing this
// component must never do is distort the shop's identity.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import clsx from 'clsx';

interface YaghiLogoProps {
  /// Rendered height in pixels. Width follows the artwork's own proportions.
  height?: number;
  /// Use the cream wordmark on the dark green sidebar, the dark one elsewhere.
  /// Only affects the TEXT FALLBACK - the real artwork is never recoloured.
  variant?: 'dark' | 'light';
  className?: string;
  /// Set false where the logo sits beside the shop name and would only repeat
  /// it to a screen reader.
  decorative?: boolean;
  /// Marks the logo as belonging to a printed receipt, so the print rules can
  /// keep it while hiding everything else.
  forPrint?: boolean;
}

export function YaghiLogo({
  height = 40,
  variant = 'dark',
  className,
  decorative = false,
  forPrint = false,
}: YaghiLogoProps) {
  const [failed, setFailed] = useState(false);

  if (!failed) {
    return (
      <img
        src="/images/yaghi-logo.png"
        alt={decorative ? '' : 'Yaghi Butcher'}
        aria-hidden={decorative ? 'true' : undefined}
        // Height only. No width, so the browser keeps the original ratio.
        style={{ height, width: 'auto' }}
        className={clsx('select-none object-contain', forPrint && 'receipt-logo', className)}
        onError={() => setFailed(true)}
        draggable={false}
      />
    );
  }

  // --- fallback wordmark ---------------------------------------------------
  // Deliberately plain. It should look like the artwork is missing rather than
  // pretend to be the logo.
  return (
    <span
      className={clsx(
        'inline-flex select-none items-baseline font-bold tracking-tight',
        forPrint && 'receipt-logo',
        className,
      )}
      style={{ fontSize: height * 0.55, lineHeight: 1 }}
      aria-label={decorative ? undefined : 'Yaghi Butcher'}
      aria-hidden={decorative ? 'true' : undefined}
      role={decorative ? undefined : 'img'}
    >
      <span className={variant === 'light' ? 'text-warm-cream' : 'text-rich-brown'}>Y</span>
      <span className="text-burgundy-red">a</span>
      <span className={variant === 'light' ? 'text-antique-gold' : 'text-butcher-green'}>gh</span>
      <span className="text-burgundy-red">i</span>
    </span>
  );
}
